import { useCallback, useEffect, useState } from 'react'

// Tema dual claro/escuro. Preferência salva em localStorage ('light'|'dark');
// ausência = segue o sistema (prefers-color-scheme). O tema resolvido é aplicado
// em <html data-theme="…">, que troca os tokens de superfície (src/index.css) e
// ativa as variantes `dark:` do Tailwind. O 1º paint já vem correto via script
// inline no index.html (anti-FOUC); este hook mantém o React em sincronia.

type ThemePref = 'light' | 'dark' | 'system'
type Resolved = 'light' | 'dark'

const STORAGE_KEY = 'bx:theme'

function systemPrefersDark(): boolean {
  return typeof window !== 'undefined'
    && !!window.matchMedia
    && window.matchMedia('(prefers-color-scheme: dark)').matches
}

function readPref(): ThemePref {
  try {
    const v = localStorage.getItem(STORAGE_KEY)
    return v === 'light' || v === 'dark' ? v : 'system'
  } catch {
    return 'system'
  }
}

function resolve(pref: ThemePref): Resolved {
  if (pref === 'light' || pref === 'dark') return pref
  return systemPrefersDark() ? 'dark' : 'light'
}

function apply(resolved: Resolved) {
  document.documentElement.dataset.theme = resolved
  // Barra do navegador / PWA acompanha o tema.
  const meta = document.querySelector('meta[name="theme-color"]')
  if (meta) meta.setAttribute('content', resolved === 'dark' ? '#0A1420' : '#0D1B2A')
}

export function useTheme() {
  const [pref, setPref] = useState<ThemePref>(() => readPref())
  const [resolved, setResolved] = useState<Resolved>(() => resolve(readPref()))

  useEffect(() => {
    const r = resolve(pref)
    setResolved(r)
    apply(r)
  }, [pref])

  // No modo "system", reage ao SO mudando de claro/escuro ao vivo.
  useEffect(() => {
    if (pref !== 'system' || !window.matchMedia) return
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = () => { const r: Resolved = mq.matches ? 'dark' : 'light'; setResolved(r); apply(r) }
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [pref])

  /** Define explicitamente o tema. 'system' volta a seguir o SO (limpa o storage). */
  const setTheme = useCallback((next: ThemePref) => {
    try {
      if (next === 'system') localStorage.removeItem(STORAGE_KEY)
      else localStorage.setItem(STORAGE_KEY, next)
    } catch { /* storage indisponível — segue só em memória */ }
    setPref(next)
  }, [])

  /** Alterna claro↔escuro (vira escolha explícita, persistida). */
  const toggle = useCallback(() => {
    const next: ThemePref = resolve(readPref()) === 'dark' ? 'light' : 'dark'
    try { localStorage.setItem(STORAGE_KEY, next) } catch { /* noop */ }
    setPref(next)
  }, [])

  return { theme: pref, resolvedTheme: resolved, setTheme, toggle }
}
